import { getCurrentPositionAsync } from 'expo-location';
import { useContext, useRef } from 'react';
import { useLocationServices } from 'shared/hooks/maps/useLocationServices';
import useZipCodeCoordinates, {
  LocationCoordinates,
} from 'shared/hooks/maps/useZipCodeCoordinates';
import { AbortError } from 'shared/hooks/useCancellableMutation/useCancellableMutation';
import {
  EnvironmentContext,
  EnvironmentContextProps,
} from 'shared/providers/environment';
import {
  logErrorWithContext,
  logNonFatalError,
} from 'shared/utils/sentry/sentry';
import {
  ErrorSeverity,
  FeatureArea,
  ResponsibleTeam,
} from 'shared/utils/sentry/sentry.constants';

export type { LocationCoordinates };

interface GetLocationCoordinatesParams {
  shouldRequestPermission: boolean;
}

interface GetLocationCoordinatesResult {
  coordinates: LocationCoordinates | null;
  hasPermission: boolean;
}

const getLocationFromGPS = async (): Promise<LocationCoordinates | null> => {
  try {
    const position = await getCurrentPositionAsync({});
    return {
      lat: position.coords.latitude,
      lng: position.coords.longitude,
    };
  } catch (error) {
    logErrorWithContext(error, {
      featureArea: FeatureArea.REWARDS,
      responsibleTeam: ResponsibleTeam.CASHBACK_REWARDS,
      severity: ErrorSeverity.LOW,
    });
    return null;
  }
};

export const useGetLocationCoordinates = () => {
  const hasLocationFetched = useRef(false);
  const getZipCodeCoordinates = useZipCodeCoordinates();

  const { currentEnv, getMapsUrl } =
    useContext<EnvironmentContextProps>(EnvironmentContext);
  const mapsUrl = getMapsUrl(currentEnv);

  const { access, requestPermission } = useLocationServices();

  const getLocationFromZipCode =
    async (): Promise<LocationCoordinates | null> => {
      try {
        return await getZipCodeCoordinates(mapsUrl, {
          onMissingZipCode: () =>
            logErrorWithContext('Zip code is null or empty', {
              featureArea: FeatureArea.REWARDS,
              responsibleTeam: ResponsibleTeam.CASHBACK_REWARDS,
              severity: ErrorSeverity.LOW,
            }),
        });
      } catch (error) {
        if (error instanceof AbortError) {
          return null;
        }

        logErrorWithContext(error, {
          featureArea: FeatureArea.REWARDS,
          responsibleTeam: ResponsibleTeam.CASHBACK_REWARDS,
          severity: ErrorSeverity.LOW,
        });
        return null;
      }
    };

  const getLocationCoordinates = async ({
    shouldRequestPermission,
  }: GetLocationCoordinatesParams): Promise<GetLocationCoordinatesResult> => {
    let isGranted = access === 'granted';

    // Request permission if not granted and feature flag is enabled
    if (access === 'permissionDenied' && shouldRequestPermission) {
      const granted = await requestPermission();

      if (!granted) {
        const coordinates = await getLocationFromZipCode();
        return { coordinates, hasPermission: false };
      }

      isGranted = granted;
    }

    if (!isGranted) {
      const coordinates = await getLocationFromZipCode();
      return { coordinates, hasPermission: false };
    }

    try {
      // Try to get GPS coordinates first
      const gpsCoordinates = await getLocationFromGPS();
      if (gpsCoordinates) {
        return { coordinates: gpsCoordinates, hasPermission: true };
      }
    } catch (error) {
      logNonFatalError(error instanceof Error ? error : String(error));
    }

    // Fallback to zip code if GPS fails
    const coordinates = await getLocationFromZipCode();
    return { coordinates, hasPermission: isGranted };
  };

  return { access, hasLocationFetched, getLocationCoordinates };
};
